import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Settings2, Pencil, Plus, Repeat, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { useBulkDeleteCli, useCliList, useDeallocateCli, useRemoveDedicatedCli } from '../api';
import { cliStatusLabel, type CliRow, type CliType } from '../types';
import { useModuleFilter } from '@/lib/permissions/use-module-filter';
import { MODULE_LISTING_KEYS, BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';

export function CliListPage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const userId = useAuthStore((s) => s.userId);
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const isAdmin = role === USER_ROLES.ADMIN;
  const isUser = role === USER_ROLES.USER;
  const canSwitch = !isUser;

  // SADMIN can toggle; SUB_SUPER_ADMIN defaults to 'cli', ADMIN to 'dedicated_cli'.
  const initialCliType: CliType = isAdmin ? 'dedicated_cli' : 'cli';
  const [cliType, setCliType] = useState<CliType>(initialCliType);

  const query = useCliList(cliType);
  const filterKey = (cliType as string) === 'api' ? MODULE_LISTING_KEYS.MANAGE_API_CLI : MODULE_LISTING_KEYS.MANAGE_CLI;
  const rows = useModuleFilter(query.data ?? [], filterKey, (c) => c.cliId);
  const canUploadCli = usePermission('Manage CLI.Upload CLI', BASE_PERMISSION.ADD);
  const canAllocateCli = usePermission('Manage CLI.Allocate CLI', BASE_PERMISSION.ADD);
  const canEditCli = usePermission('Manage CLI.CLI List', BASE_PERMISSION.EDIT);
  const canDeleteCli = usePermission('Manage CLI.CLI List', BASE_PERMISSION.DELETE);

  const deallocate = useDeallocateCli();
  const removeDedicated = useRemoveDedicatedCli();
  const bulkDelete = useBulkDeleteCli();

  const [pendingDelete, setPendingDelete] = useState<CliRow | null>(null);
  const [pendingBulk, setPendingBulk] = useState<{ ids: string[]; clear: () => void } | null>(
    null,
  );

  const columns = useMemo<ColumnDef<CliRow>[]>(() => {
    const base: ColumnDef<CliRow>[] = [
      {
        accessorKey: 'cliId',
        header: 'CLI ID',
        size: 100,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.cliId}
          </span>
        ),
      },
      { accessorKey: 'cli', header: 'CLI' },
    ];

    if (isSubOrSuper || isAdmin) {
      base.push({
        accessorKey: 'cliStatus',
        header: 'Status',
        size: 120,
        cell: ({ row }) => cliStatusLabel(row.original.cliStatus),
      });
    }

    if (!isUser) {
      base.push(
        { accessorKey: 'dialerName', header: 'Dialer', size: 160 },
        { accessorKey: 'pilotNumber', header: 'Pilot No.', size: 140 },
      );
    }

    if (cliType === 'dedicated_cli') {
      base.push(
        {
          id: 'userName',
          header: 'Username',
          size: 160,
          accessorFn: (r) =>
            r.cliStatus === 2 ? r.userName ?? '' : r.resellerUsername ?? '',
        },
        {
          accessorKey: 'allocationDate',
          header: 'Allocated',
          size: 180,
        },
      );
    }

    if ((isSubOrSuper && (canEditCli || canDeleteCli)) || (isAdmin && cliType === 'dedicated_cli')) {
      base.push({
        id: 'actions',
        header: '',
        size: 60,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Row actions"
              >
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="font-mono">
                {row.original.cli}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {isSubOrSuper && canEditCli && (
                <DropdownMenuItem
                  onClick={() => navigate(`/update-cli/${row.original.cliId}`)}
                >
                  <Pencil className="h-4 w-4" />
                  Edit value
                </DropdownMenuItem>
              )}
              {(canDeleteCli || isAdmin) && (
                <DropdownMenuItem
                  onClick={() => setPendingDelete(row.original)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  {cliType === 'cli' ? 'Delete' : 'Deallocate'}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      });
    }

    return base;
  }, [cliType, isSubOrSuper, isAdmin, isUser, navigate, canEditCli, canDeleteCli]);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    if (cliType === 'dedicated_cli') {
      // Move back to the shared pool (+ CLI log) instead of deleting the row.
      const target = pendingDelete.cliStatus === 2 ? pendingDelete.userId : pendingDelete.parent;
      removeDedicated.mutate(
        { userId: String(target ?? ''), cliIds: String(pendingDelete.cliId) },
        {
          onSuccess: (res) => {
            if (res?.message !== 'Cli Removed Successfully') {
              toast.error(res?.message ?? 'Could not deallocate the CLI.');
              return;
            }
            toast.success('CLI deallocated. It is back in the shared list.');
            setPendingDelete(null);
          },
          onError: () => toast.error('Could not deallocate the CLI.'),
        },
      );
      return;
    }
    deallocate.mutate(
      {
        cliId: pendingDelete.cliId,
        userId: Number(userId),
        allocated: isSubOrSuper ? 1 : 2,
        // All CLIs (free shared pool) → sirf delete, log nahi
        noLog: cliType === 'cli',
      },
      {
        onSuccess: (res) => {
          toast.success(
            res?.message ??
              (cliType === 'cli'
                ? `Deleted ${pendingDelete.cli}`
                : `Deallocated ${pendingDelete.cli}`),
          );
          setPendingDelete(null);
        },
        onError: () =>
          toast.error(
            cliType === 'cli'
              ? 'Could not delete the CLI.'
              : 'Could not deallocate the CLI.',
          ),
      },
    );
  };

  const confirmBulkDelete = () => {
    if (!pendingBulk) return;
    bulkDelete.mutate(pendingBulk.ids.map(Number), {
      onSuccess: (res) => {
        toast.success(res?.message ?? `Deleted ${pendingBulk.ids.length} CLIs`);
        pendingBulk.clear();
        setPendingBulk(null);
      },
      onError: () => toast.error('Bulk delete failed.'),
    });
  };

  const canView = usePermission('Manage CLI.CLI List', BASE_PERMISSION.VIEW);

  if (!canView) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title={cliType === 'cli' ? 'All CLIs' : 'Dedicated CLIs'}
        description="All caller-line identities visible to your account."
        actions={
          <div className="flex items-center gap-2">
            {canSwitch && (
              <Button
                variant="outline"
                onClick={() =>
                  setCliType((t) => (t === 'cli' ? 'dedicated_cli' : 'cli'))
                }
              >
                <Repeat className="h-4 w-4" />
                {cliType === 'cli' ? 'Show dedicated' : 'Show shared'}
              </Button>
            )}
            {(isSubOrSuper || isAdmin) && (
              <>
                {(isAdmin || canAllocateCli) && (
                  <Button asChild variant="outline">
                    <Link to="/add-cli">
                      <Plus className="h-4 w-4" />
                      Allocate to user
                    </Link>
                  </Button>
                )}
                {isSubOrSuper && canUploadCli && (
                  <Button asChild>
                    <Link
                      to={cliType === 'cli' ? '/upload-cli' : '/upload-dedicated-cli'}
                    >
                      <Upload className="h-4 w-4" />
                      Upload {cliType === 'cli' ? 'CLI' : 'dedicated CLI'}
                    </Link>
                  </Button>
                )}
              </>
            )}
          </div>
        }
      />

      <DataTable<CliRow, unknown>
        tableId={`cli:list:${cliType}:v2`}
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search CLIs…"
        emptyTitle="No CLIs found"
        emptyDescription="Upload a CLI file or allocate CLIs to populate this list."
        getRowId={(row) => String(row.cliId)}
        exportFilename={`${cliType}.csv`}
        initialColumnPinning={isSubOrSuper || isAdmin ? { right: ['actions'] } : undefined}
        pageSize={10}
        enableRowSelection={isSubOrSuper}
        renderSelectionActions={(ids, clear) =>
          canDeleteCli ? (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setPendingBulk({ ids, clear })}
              disabled={ids.length === 0 || bulkDelete.isPending}
            >
              <Trash2 className="h-4 w-4" />
              Delete {ids.length}
            </Button>
          ) : null
        }
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            CLI <span className="font-medium text-foreground">{pendingDelete?.cli}</span> will
            be {cliType === 'cli' ? 'deleted permanently' : 'deallocated'}.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deallocate.isPending || removeDedicated.isPending}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={pendingBulk !== null}
        onOpenChange={(o) => !o && setPendingBulk(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            <span className="font-medium text-foreground">
              {pendingBulk?.ids.length ?? 0}
            </span>{' '}
            selected CLIs will be permanently deleted.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={bulkDelete.isPending}
        onConfirm={confirmBulkDelete}
      />
    </FillPage>
  );
}
